import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  House,
  Coins,
  Article,
  SignOut,
  ShieldStar,
  ListMagnifyingGlass,
  Scales,
  Gear,
  Files,
  Receipt,
  Users,
  BookOpen,
  Bell,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: House, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/board-rates", label: "Board Rates", icon: Coins },
  { to: "/admin/posts", label: "Posts", icon: Article },
  { to: "/admin/audit-logs", label: "Audit Log", icon: ListMagnifyingGlass },
  { to: "/admin/legal", label: "Legal", icon: Scales },
  { to: "/admin/knowledge", label: "Knowledge", icon: BookOpen },
  { to: "/admin/alerts", label: "Alerts", icon: Bell },
  { to: "/admin/documents", label: "Documents", icon: Files },
  { to: "/admin/quotes", label: "Quotes", icon: Receipt },
  { to: "/admin/settings", label: "Settings", icon: Gear },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="min-h-screen flex" data-testid="admin-layout">
      <aside className="w-60 shrink-0 border-r border-[#E8DFD1] bg-white hidden md:flex flex-col">
        <Link to="/" className="px-5 py-5 border-b border-[#E8DFD1] flex items-center gap-2" data-testid="admin-logo">
          <img src="/mineai-logo.jpeg" alt="Mine AI" className="h-7 w-auto" />
        </Link>

        <div className="px-5 py-4 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#3B5BDB] font-semibold border-b border-[#E8DFD1]">
          <ShieldStar size={14} weight="fill" />
          Admin
        </div>

        <nav className="flex-1 p-3 flex flex-col gap-1">
          {ADMIN_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm smooth ${
                  isActive
                    ? "bg-[#3B5BDB] text-white"
                    : "text-[#0A1128]/80 hover:bg-[#F5EFE6]"
                }`
              }
            >
              <n.icon size={18} weight="duotone" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-[#E8DFD1]">
          <p className="text-xs text-[#4A5568] truncate">{user?.email}</p>
          <p className="text-[11px] text-[#4A5568]/70 mt-0.5 capitalize">{user?.role}</p>
          <button
            data-testid="admin-logout"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="mt-3 w-full text-left text-xs text-[#DC2626] flex items-center gap-1 hover:opacity-70 smooth"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden border-b border-[#E8DFD1] bg-white px-5 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/mineai-logo.jpeg" alt="Mine AI" className="h-6 w-auto" />
          </Link>
          <span className="text-xs uppercase tracking-wider text-[#3B5BDB] font-semibold">Admin</span>
        </div>
        <div className="md:hidden border-b border-[#E8DFD1] bg-[#F5EFE6]/40 px-3 py-2 flex gap-2 overflow-x-auto">
          {ADMIN_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-full text-xs whitespace-nowrap smooth ${
                  isActive ? "bg-[#3B5BDB] text-white" : "bg-white border border-[#E8DFD1]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <main className="p-5 sm:p-8 lg:p-10">{children}</main>
      </div>
    </div>
  );
}
